import { useState, useMemo, useEffect } from 'react';
import { Table, Form, Button, InputGroup, Row, Col, Pagination } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getEntities } from '../utils/api';
import Header from './Header';
import Loader from './Loader';
import { faPlus, faPencil, faCity, faX, faFileLines, faFileCode, faGear } from '@fortawesome/free-solid-svg-icons';
import ShowMenuButton from './ShowMenuButton';
import CreateEditEntityModal from './Modals/CreateEditEntityModal';
import EditEntityDocumentSettingsModal from './Modals/EditEntityDocumentSettingsModal';
import Error from './Error';
import { useDataStore } from '../Store';
import ViewEntityModal from './Modals/ViewEntityModal';

const Entities = () => {

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);

    const [selectedEntity, setSelectedEntity] = useState(null);
    const [showEditEntitiesModal, setShowEditEntitiesModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);

    const fetchData = async () => {
        const data = await getEntities(page, 30, searchText);
        if (data) {
            setData(data);
        }
        else {
            setError('Failed to fetch data');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [searchText, page]);


    const createEntity = () => {
        setSelectedEntity(null)
        setShowEditEntitiesModal(true);
    };
    const rowClicked = (o) => {
        setSelectedEntity(o);
        setShowViewModal(true);
    }
    const handleActionClick = (name, o) => {
        if (name === 'View') {
            rowClicked(o);
            return;
        }
        if (name === 'Edit') {
            setSelectedEntity(o);
            setShowEditEntitiesModal(true);
            return;
        }
    }

    const buttons = [
        { id: 1, label: 'Add Entity', variant: 'primary', icon: faPlus, onClick: () => createEntity() },
    ]

    const lastPage = data.LastPage;

    return (
        <div style={{ padding: 10 }}>
            {loading ? <Loader /> : error ? <Error /> : (
                <>
                    <Header title='Entities' description="Manage entities" buttons={buttons} />
                    <Row className="mb-3">
                        <Col md={12}>
                            <InputGroup>
                                <Form.Control
                                    type="text"
                                    placeholder="Search entities..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            fetchData();
                                        }
                                    }}
                                />
                                <Button variant="outline-secondary" onClick={fetchData}>
                                    Search
                                </Button>
                            </InputGroup>
                        </Col>
                    </Row>

                    <Row className="mb-3 mt-3">
                        <Col>
                            <div style={{ overflowX: 'auto', width: '100%' }}>
                                <Table striped bordered hover style={{ minHeight: 190, overflow: 'visible' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                ID
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Name
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Legal Name
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Tax Id
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Address
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Address2
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                City
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                State
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Zip
                                            </th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.Results?.map((d, idx) => (
                                            <tr key={idx}>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' , cursor:'pointer' }} onClick={() => rowClicked(d)}>{d?.Id}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' , cursor:'pointer' }} onClick={() => rowClicked(d)}>{d?.Name}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' , cursor:'pointer' }} onClick={() => rowClicked(d)}>{d?.LegalName}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' , cursor:'pointer' }} onClick={() => rowClicked(d)}>{d?.TaxId}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' , cursor:'pointer' }} onClick={() => rowClicked(d)}>{d?.Address?.AddressLine1}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' , cursor:'pointer' }} onClick={() => rowClicked(d)}>{d?.Address?.AddressLine2}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' , cursor:'pointer' }} onClick={() => rowClicked(d)}>{d?.Address?.City}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' , cursor:'pointer' }} onClick={() => rowClicked(d)}>{d?.Address?.State}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' , cursor:'pointer' }} onClick={() => rowClicked(d)}>{d?.Address?.PostalCode}</td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center', verticalAlign: 'middle' }}>{<ShowMenuButton menuItems={[
                                                    { icon: <FontAwesomeIcon icon={faCity} />, name: 'View', target: d, onClick: handleActionClick },
                                                    { icon: <FontAwesomeIcon icon={faPencil} />, name: 'Edit', target: d, onClick: handleActionClick },
                                                ]} />}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    </Row>

                    <Row className="mb-3 mt-3">
                        <Col className="d-flex justify-content-center">
                            <Pagination>
                                <Pagination.First disabled={page <= 1} onClick={() => setPage(1)} />
                                <Pagination.Prev disabled={page <= 1} onClick={() => setPage(page - 1)} />
                                <Pagination.Item key={page} active={true}>
                                    {page}
                                </Pagination.Item>
                                <Pagination.Next disabled={page >= lastPage} onClick={() => setPage(page + 1)} />
                                <Pagination.Last disabled={page >= lastPage} onClick={() => setPage(lastPage)} />
                            </Pagination>
                        </Col>
                    </Row>

                    <ViewEntityModal
                        show={showViewModal}
                        onHide={() => setShowViewModal(false)}
                        entity={selectedEntity}
                    />



                    <CreateEditEntityModal
                        show={showEditEntitiesModal}
                        onHide={(shouldRefresh) => {
                            setShowEditEntitiesModal(false);
                            if (shouldRefresh)
                                window.location.reload();//so me is called again
                        }}
                        entity={selectedEntity}
                    />

                </>
            )}
        </div>
    );
};

export default Entities;
